import { CreatePageState } from "@/stores/createPageStore";
import { generatePresets } from "@/services/createAgentService.presets";
import { detectPhase } from "@/services/createAgentService.phase";
import {
  SerializableAgentMessage,
  SerializablePresetOption,
  CreateFormData,
} from "@/components/create/types";

function hasUploadedImageReferences(
  formData: Partial<CreateFormData> | undefined
): boolean {
  return !!(
    (formData?.uploadedFrontImages &&
      formData.uploadedFrontImages.length > 0) ||
    (formData?.uploadedBackImages && formData.uploadedBackImages.length > 0) ||
    (formData?.uploadedFrontImageUrls &&
      formData.uploadedFrontImageUrls.length > 0) ||
    (formData?.uploadedBackImageUrls &&
      formData.uploadedBackImageUrls.length > 0)
  );
}

export interface SerializedCreateDraftSnapshot {
  formData?: Partial<CreateFormData>;
  designBrief?: unknown;
  imageUrl?: string | null;
  imagePath?: string | null;
  backImageUrl?: string | null;
  backImagePath?: string | null;
  frontVariations?: unknown[] | null;
  backVariations?: unknown[] | null;
  chatHistory?: unknown[] | null;
  pendingComposerRecovery?: unknown;
}

/**
 * One definition of a draft worth restoring, shared by live Zustand state and
 * serialized SavePoints. In particular, a style-only or reference-only draft
 * is meaningful even before a chat session or generated render exists.
 */
export function hasMeaningfulSerializedFormState(
  snapshot: SerializedCreateDraftSnapshot | null | undefined
): boolean {
  if (!snapshot) return false;

  const formData = snapshot.formData;
  const hasPrompt = Boolean(formData?.prompt?.trim());
  const hasStyleFields = Boolean(
    formData?.color ||
    formData?.length ||
    formData?.sleeve ||
    formData?.sleeveAddons?.length ||
    formData?.neckline ||
    formData?.waistline ||
    formData?.silhouette ||
    formData?.fabric ||
    formData?.backPrompt ||
    formData?.preset_type ||
    formData?.isUploadPreset ||
    formData?.isFrontUploadPreset ||
    formData?.isBackUploadPreset ||
    formData?.isFrontCustomPreset ||
    formData?.isBackCustomPreset
  );
  const hasGeneratedImages = Boolean(
    snapshot.imageUrl ||
    snapshot.imagePath ||
    snapshot.backImageUrl ||
    snapshot.backImagePath
  );
  const hasVariations = Boolean(
    snapshot.frontVariations?.length || snapshot.backVariations?.length
  );
  const hasChatHistory = Boolean(
    snapshot.chatHistory?.length || formData?.chatHistory?.length
  );

  return Boolean(
    hasPrompt ||
    hasStyleFields ||
    hasUploadedImageReferences(formData) ||
    hasGeneratedImages ||
    hasVariations ||
    hasChatHistory ||
    snapshot.pendingComposerRecovery ||
    snapshot.designBrief
  );
}

/**
 * Checks if the form state has meaningful data worth saving
 */
export function hasMeaningfulFormData(state: CreatePageState): boolean {
  return hasMeaningfulSerializedFormState({
    formData: state.form.data,
    designBrief: state.agent.designBrief,
    imageUrl: state.generation.images.front.url,
    imagePath: state.generation.images.front.path,
    backImageUrl: state.generation.images.back.url,
    backImagePath: state.generation.images.back.path,
    frontVariations: state.generation.images.variations.front,
    backVariations: state.generation.images.variations.back,
    chatHistory: state.agent.conversation.messages,
    pendingComposerRecovery: state.agent.pendingComposerRecovery,
  });
}

/**
 * Serializes form state for saving to SavePoint
 */
export function serializeFormState(state: CreatePageState) {
  // Serialize from agent state (single source of truth)
  const serializedChatHistory: SerializableAgentMessage[] | undefined =
    state.agent.conversation.messages.length > 0
      ? state.agent.conversation.messages.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp.toISOString(),
          imageUrl: msg.imageUrl,
          imageUrls: msg.imageUrls,
          imageSide: msg.imageSide,
          selectedIndex: msg.selectedIndex,
          hidden: msg.hidden,
          presets: msg.presets?.map(preset => ({
            label: preset.label,
            action: preset.action,
            actionId: preset.actionId,
            presetType: preset.presetType,
            data: preset.data,
            actionData: preset.actionData,
          })),
          widgets: msg.widgets,
          sources: msg.sources,
          thinkingTrace: msg.thinkingTrace,
        }))
      : undefined;

  // Serialize from agent state (single source of truth)
  const serializedQuickActions: SerializablePresetOption[] | undefined =
    state.agent.presets.length > 0
      ? state.agent.presets.map(preset => ({
          label: preset.label,
          action: preset.action,
          actionId: preset.actionId,
          presetType: preset.presetType,
          data: preset.data,
          actionData: preset.actionData,
        }))
      : undefined;
  const fallbackQuickActions =
    !serializedQuickActions && !state.agent.conversation.presetsLoading
      ? generatePresets(
          (() => {
            const hasFrontImage = Boolean(
              state.generation.images.front.url ||
              state.generation.images.front.path
            );
            const hasBackImage = Boolean(
              state.generation.images.back.url ||
              state.generation.images.back.path
            );
            const detectedPhase = detectPhase(state.form.data, {
              hasFrontImage,
              hasBackImage,
            });
            const hasPresetType = Boolean(
              (state.form.data as any)?.preset_type
            );
            return detectedPhase === "initial" && hasPresetType
              ? "front_design"
              : state.agent.phase || detectedPhase;
          })(),
          state.form.data
        ).map(preset => ({
          label: preset.label,
          action: preset.action,
          actionId: preset.actionId,
          presetType: preset.presetType,
          data: preset.data,
          actionData: preset.actionData,
        }))
      : undefined;

  return {
    formData: {
      ...state.form.data,
      uploadedFrontImages: [],
      uploadedBackImages: [],
      chatHistory: serializedChatHistory,
      quick_action_buttons: serializedQuickActions || fallbackQuickActions,
      // Keep legacy field for backward compatibility
      presets: serializedQuickActions || fallbackQuickActions,
    },
    currentStep: state.form.currentStep,
    designBrief: state.agent.designBrief,
    imageUrl: state.generation.images.front.url,
    imagePath: state.generation.images.front.path,
    frontDesignSpec: state.generation.images.front.designSpec,
    backImageUrl: state.generation.images.back.url,
    backImagePath: state.generation.images.back.path,
    backDesignSpec: state.generation.images.back.designSpec,
    originalImageUrl: state.generation.images.front.original.url,
    originalImagePath: state.generation.images.front.original.path,
    originalFrontDesignSpec: state.generation.images.front.original.designSpec,
    originalBackImageUrl: state.generation.images.back.original.url,
    originalBackImagePath: state.generation.images.back.original.path,
    originalBackDesignSpec: state.generation.images.back.original.designSpec,
    ticketId: state.ticket.id,
    modifyPrompt: state.ui.modification.prompt,
    showModifyInput: state.ui.modification.showInput,
    currentView: state.ui.view,
    frontVariations:
      state.generation.images.variations.front.length > 0
        ? state.generation.images.variations.front
        : null,
    backVariations:
      state.generation.images.variations.back.length > 0
        ? state.generation.images.variations.back
        : null,
    selectedFrontVariationIndex: state.generation.images.selected.frontIndex,
    selectedBackVariationIndex: state.generation.images.selected.backIndex,
    agentSessionId: state.agent.sessionId,
    agentSessionVersion: state.agent.sessionVersion,
    agentMessageVersion: state.agent.messageVersion,
    agentPhase: state.agent.phase,
    pendingSubmittedMessage: state.agent.pendingSubmittedMessage,
    pendingSubmittedMessageId: state.agent.pendingSubmittedMessageId,
    pendingComposerSeed: state.agent.pendingComposerRecovery
      ? state.agent.pendingComposerSeed
      : null,
    pendingComposerRecovery: state.agent.pendingComposerRecovery
      ? {
          ...state.agent.pendingComposerRecovery,
          ...(state.agent.pendingComposerRecovery.formStateOverride && {
            formStateOverride: {
              ...state.agent.pendingComposerRecovery.formStateOverride,
              // Browser File objects are not JSON-restorable. The uploaded URL
              // references in the same recovery object are the durable source.
              uploadedFrontImages: [],
              uploadedBackImages: [],
            },
          }),
        }
      : null,
  };
}

export function prepareSavePointPayload(
  state: CreatePageState,
  targetStep?: number
) {
  const serializedState = serializeFormState(state);

  return {
    ...serializedState,
    currentStep: targetStep ?? serializedState.currentStep,
  };
}

/**
 * Preserve the generated ticket while an existing-account sign-in performs an
 * atomic server-side ownership adoption. The redirect reports adoption failure
 * explicitly; restoration clears this id only in that fallback case so a
 * successful adoption does not create a duplicate request.
 */
export function prepareQuoteAuthRedirectPayload(state: CreatePageState) {
  return prepareSavePointPayload(state, 1);
}
